import React, { useState } from "react";
import Modal from "../../common/modal";
import Button from "../../common/button";
import theme from "../../config/theme";
import { IoIosCheckmark } from "react-icons/io";

export interface Template {
  id: string;
  name: string;
  type: "large" | "small";
  layout: string; // e.g., '2x3', '2x2', etc.
}

export interface UploadedImageSize {
  width: number;
  height: number;
}

export interface TemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (template: Template) => void;
  /** If provided, only templates matching this image size will be enabled */
  uploadedImageSize?: UploadedImageSize | null;
  /** Current template ID to pre-select in the dialog */
  currentTemplateId?: string;
}

/**
 * Determine template type based on image dimensions
 * Large: 2500 x 1686 (aspect ratio ~1.48)
 * Small: 2500 x 843 (aspect ratio ~2.97)
 */
export const getTemplateTypeFromImageSize = (
  width: number,
  height: number
): "large" | "small" | null => {
  // Calculate aspect ratio
  const aspectRatio = width / height;

  // Large template: height around 1686 (aspect ratio ~1.48)
  // Small template: height around 843 (aspect ratio ~2.97)

  // Allow some tolerance for aspect ratio
  if (aspectRatio >= 1.3 && aspectRatio <= 1.7) {
    return "large"; // ~1.48 aspect ratio
  } else if (aspectRatio >= 2.5 && aspectRatio <= 3.5) {
    return "small"; // ~2.97 aspect ratio
  }

  return null; // Unknown size
};

const templates: Template[] = [
  // LINE Rich Menu (Large)
  { id: "large-2x3", name: "2x3 Grid", type: "large", layout: "2x3" },
  { id: "large-2x2", name: "2x2 Grid", type: "large", layout: "2x2" },
  {
    id: "large-1-2equal",
    name: "1 Wide + 2 Equal",
    type: "large",
    layout: "1-2equal",
  },
  {
    id: "large-1-2unequal",
    name: "1 Wide + 2 Unequal",
    type: "large",
    layout: "1-2unequal",
  },
  { id: "large-2rows", name: "2 Rows", type: "large", layout: "2rows" },
  { id: "large-2cols", name: "2 Columns", type: "large", layout: "2cols" },
  { id: "large-1rect", name: "1 Rectangle", type: "large", layout: "1rect" },

  // LINE Rich Menu (Small)
  { id: "small-1x3", name: "1x3 Grid", type: "small", layout: "1x3" },
  {
    id: "small-1-2equal",
    name: "1 Wide + 2 Equal",
    type: "small",
    layout: "1x2bigRight",
  },
  {
    id: "small-1-2unequal",
    name: "1 Wide + 2 Unequal",
    type: "small",
    layout: "1x2bigLeft",
  },
  { id: "small-2rows", name: "2 Rows", type: "small", layout: "1x2equal" },
  {
    id: "small-1rect",
    name: "1 Rectangle",
    type: "small",
    layout: "1rectSmall",
  },
];

const TemplateDialog: React.FC<TemplateDialogProps> = ({
  isOpen,
  onClose,
  onApply,
  uploadedImageSize,
  currentTemplateId,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>(
    currentTemplateId || "large-2cols"
  );

  // Update selected template when currentTemplateId changes
  React.useEffect(() => {
    if (currentTemplateId) {
      setSelectedTemplate(currentTemplateId);
    }
  }, [currentTemplateId]);

  // Determine which template type is allowed based on uploaded image
  const allowedTemplateType = uploadedImageSize
    ? getTemplateTypeFromImageSize(
        uploadedImageSize.width,
        uploadedImageSize.height
      )
    : null; // null means all templates are allowed

  // Check if a template is disabled
  const isTemplateDisabled = (template: Template): boolean => {
    if (!uploadedImageSize || !allowedTemplateType) {
      return false; // No image uploaded or unknown size, all templates enabled
    }
    return template.type !== allowedTemplateType;
  };

  const handleApply = () => {
    const template = templates.find((t) => t.id === selectedTemplate);
    if (template) {
      onApply(template);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const templateLayouts: Record<string, () => React.ReactNode> = {
    // Large Templates
    "2x3": () => (
      <div className="grid grid-cols-3 grid-rows-2 mt-[1px] ml-[1px]">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="bg-white border border-black -mt-[1px] -ml-[1px]"
            style={{ aspectRatio: "1" }}
          />
        ))}
      </div>
    ),
    "2x2": () => (
      <div className="grid grid-cols-2 grid-rows-2 mt-[1px] ml-[1px]">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="bg-white border border-black -mt-[1px] -ml-[1px]"
            style={{ aspectRatio: "1.48" }}
          />
        ))}
      </div>
    ),
    "1-2equal": () => (
      <div className="grid grid-cols-2 grid-rows-2 mt-[1px] ml-[1px]">
        <div
          className="col-span-2 bg-white border border-black -mt-[1px] -ml-[1px]"
          style={{ aspectRatio: "2/0" }}
        />
        <div
          className="bg-white border border-black -mt-[1px] -ml-[1px]"
          style={{ aspectRatio: "1.48" }}
        />
        <div
          className="bg-white border border-black -mt-[1px] -ml-[1px]"
          style={{ aspectRatio: "1.48" }}
        />
      </div>
    ),
    "1-2unequal": () => (
      <div className="grid grid-cols-3 grid-rows-2 mt-[1px] ml-[1px]">
        <div
          className="col-span-3 bg-white border border-black -mt-[1px] -ml-[1px]"
          style={{ aspectRatio: "3/1" }}
        />
        <div
          className="col-span-2 bg-white border border-black -mt-[1px] -ml-[1px]"
          style={{ aspectRatio: "2/1" }}
        />
        <div
          className="bg-white border border-black -mt-[1px] -ml-[1px]"
          style={{ aspectRatio: "1" }}
        />
      </div>
    ),
    "2rows": () => (
      <div className="grid grid-rows-2 mt-[1px] ml-[1px]">
        <div className="bg-white border border-black -mt-[1px] -ml-[1px] h-14" />
        <div className="bg-white border border-black -mt-[1px] -ml-[1px] h-14" />
      </div>
    ),
    "2cols": () => (
      <div className="grid grid-cols-2 mt-[1px] ml-[1px]">
        <div className="bg-white border border-black -mt-[1px] -ml-[1px] h-28" />
        <div className="bg-white border border-black -mt-[1px] -ml-[1px] h-28" />
      </div>
    ),
    "1rect": () => <div className="bg-white border border-black h-28" />,

    // Small Templates
    "1x3": () => (
      <div className="grid grid-cols-3 grid-rows-2 mt-[1px] ml-[1px]">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="bg-white border border-black -mt-[1px] -ml-[1px]"
            style={{ aspectRatio: "1" }}
          />
        ))}
      </div>
    ),
    "1x2bigRight": () => (
      <div className="grid grid-cols-3 grid-rows-1 mt-[1px] ml-[1px]">
        <div className="bg-white border border-black -mt-[1px] -ml-[1px] h-14" />
        <div className="col-span-2 bg-white border border-black -mt-[1px] -ml-[1px] h-14" />
      </div>
    ),
    "1x2bigLeft": () => (
      <div className="grid grid-cols-3 grid-rows-1 mt-[1px] ml-[1px]">
        <div className="col-span-2 bg-white border border-black -mt-[1px] -ml-[1px] h-14" />
        <div className="bg-white border border-black -mt-[1px] -ml-[1px] h-14" />
      </div>
    ),
    "1x2equal": () => (
      <div className="grid grid-cols-2 grid-rows-2 mt-[1px] ml-[1px]">
        {[...Array(2)].map((_, i) => (
          <div
            key={i}
            className="bg-white border border-black -mt-[1px] -ml-[1px]"
            style={{ aspectRatio: "1.48" }}
          />
        ))}
      </div>
    ),
    "1rectSmall": () => <div className="bg-white border border-black h-14" />,
  };

  const renderTemplatePreview = (template: Template) => {
    const layoutRenderer = templateLayouts[template.layout];
    return layoutRenderer ? layoutRenderer() : null;
  };

  const largeTemplates = templates.filter((t) => t.type === "large");
  const smallTemplates = templates.filter((t) => t.type === "small");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Template"
      message={
        <div className="flex flex-col">
          {/* Content */}
          <div className="pr-2 -mr-2">
            <div className="space-y-6">
              {/* LINE Rich Menu (Large) */}
              <div>
                <h3
                  className="text-lg font-medium mb-4 flex items-center gap-2"
                  style={{ color: theme.color.gray500 }}
                >
                  LINE Rich Menu (Large)
                  <span
                    className="text-sm font-normal"
                    style={{ color: theme.color.gray300 }}
                  >
                    (2500 x 1686 px)
                  </span>
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  {largeTemplates.map((template) => {
                    const isSelected = selectedTemplate === template.id;
                    const isDisabled = isTemplateDisabled(template);
                    return (
                      <div
                        key={template.id}
                        onClick={() =>
                          !isDisabled && setSelectedTemplate(template.id)
                        }
                        className={`relative p-3 rounded-lg border-2 transition-all ${
                          isDisabled
                            ? "cursor-not-allowed opacity-40"
                            : "cursor-pointer"
                        }`}
                        style={{
                          borderColor:
                            isSelected && !isDisabled
                              ? theme.color.successDark
                              : theme.color.gray,
                          backgroundColor:
                            isSelected && !isDisabled
                              ? theme.color.successDark + 30
                              : theme.color.gray,
                        }}
                      >
                        {isSelected && !isDisabled && (
                          <div
                            className="absolute top-[3px] right-1 rounded-full"
                            style={{
                              color: theme.color.light100,
                              backgroundColor: theme.color.successDark,
                            }}
                          >
                            {React.createElement(IoIosCheckmark as any, {
                              className: "w-6 h-6",
                            })}
                          </div>
                        )}
                        <div className="w-full">
                          {renderTemplatePreview(template)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* LINE Rich Menu (Small) */}
              <div>
                <h3
                  className="text-lg font-medium mb-4 flex items-center gap-2"
                  style={{ color: theme.color.gray500 }}
                >
                  LINE Rich Menu (Small)
                  <span
                    className="text-sm font-normal"
                    style={{ color: theme.color.gray300 }}
                  >
                    (2500 x 843 px)
                  </span>
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  {smallTemplates.map((template) => {
                    const isSelected = selectedTemplate === template.id;
                    const isDisabled = isTemplateDisabled(template);
                    return (
                      <div
                        key={template.id}
                        onClick={() =>
                          !isDisabled && setSelectedTemplate(template.id)
                        }
                        className={`relative p-3 rounded-lg border-2 transition-all ${
                          isDisabled
                            ? "cursor-not-allowed opacity-40"
                            : "cursor-pointer"
                        }`}
                        style={{
                          borderColor:
                            isSelected && !isDisabled
                              ? theme.color.successDark
                              : theme.color.gray,
                          backgroundColor:
                            isSelected && !isDisabled
                              ? theme.color.successDark + 30
                              : theme.color.gray,
                        }}
                      >
                        {isSelected && !isDisabled && (
                          <div
                            className="absolute top-[3px] right-1 rounded-full"
                            style={{
                              color: theme.color.light100,
                              backgroundColor: theme.color.successDark,
                            }}
                          >
                            {React.createElement(IoIosCheckmark as any, {
                              className: "w-6 h-6",
                            })}
                          </div>
                        )}
                        <div className="w-full h-14">
                          {renderTemplatePreview(template)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      showCloseButton={true}
      className="max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      actions={[
        {
          label: "Cancel",
          onClick: handleCancel,
          variant: "secondary",
        },
        {
          label: "Apply",
          onClick: handleApply,
          variant: "primary",
        },
      ]}
    />
  );
};

export default TemplateDialog;
